import React, { useState, useRef, useEffect } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle
} from 'lucide-react';

import { Banner } from './Banner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const nameInputRef = useRef(null);
  const formRef = useRef(null);


  useEffect(() => {
    const url = new URL(window.location.href);
    const shouldFocus = url.searchParams.get('focus') === 'contact' || url.hash === '#contact' || url.hash === '#focus';
    if (shouldFocus) {
      // Smooth scroll to form and focus the name field
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => {
        if (nameInputRef.current) nameInputRef.current.focus();
      }, 300);
    }

    // Optional: allow programmatic focusing via custom event
    const handler = () => {
      if (formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        if (nameInputRef.current) nameInputRef.current.focus();
      }, 300);
    };
    window.addEventListener('focus-contact-first', handler);
    return () => window.removeEventListener('focus-contact-first', handler);
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        console.log('Form submitted successfully:', data);
        setIsSubmitted(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        alert(data.detail || 'Failed to send message. Please try again or contact us directly.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to send message. Please check your internet connection and try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      {/* Shared Banner + Pill (reduced) with title fed into Banner */}
      <Banner pillText="Get Started" title="Schedule Your Consultation" reduced={true} />
      <section id="contact" className="pt-8 pb-20" style={{ backgroundColor: 'var(--bg-page)' }}>
        <div className="container">
        {/* Removed duplicate subtitle to avoid repetition with Banner */}
        <div className="text-center mb-8"></div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div className="bg-gray-900/50 p-8 rounded-2xl border" style={{ borderColor: 'var(--border-brick-subtle)' }}>
            <h3 className="heading-3 mb-8" style={{ color: 'var(--brand-primary)' }}>Send Us a Message</h3>
            
            {isSubmitted ? (
              <div className="text-center py-12">
                <CheckCircle className="mx-auto mb-4" style={{ color: 'var(--brand-primary)' }} size={48} />
                <h4 className="text-xl font-bold mb-2" style={{ color: 'var(--brand-primary)' }}>Message Sent!</h4>
                <p className="text-gray-400">We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      id="contact-first-name"
                      ref={nameInputRef}
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full p-4 bg-black/50 border border-gray-600 rounded-xl text-white focus:outline-none transition-colors"
                      style={{ ':focus': { borderColor: 'var(--brand-primary)' } }}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full p-4 bg-black/50 border border-gray-600 rounded-xl text-white focus:outline-none transition-colors"
                      style={{ ':focus': { borderColor: 'var(--brand-primary)' } }}
                      placeholder="Your phone number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full p-4 bg-black/50 border border-gray-600 rounded-xl text-white focus:outline-none transition-colors"
                    style={{ ':focus': { borderColor: 'var(--brand-primary)' } }}
                    placeholder="Your email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Service Needed
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full p-4 bg-black/50 border border-gray-600 rounded-xl text-white focus:outline-none transition-colors"
                    style={{ ':focus': { borderColor: 'var(--brand-primary)' } }}
                  >
                    <option value="">Select a service</option>
                    <option value="sports-injury">Sports Injury Recovery</option>
                    <option value="orthopedic">Orthopedic Rehabilitation</option>
                    <option value="manual-therapy">Manual Therapy</option>
                    <option value="vertigo">Vertigo & Dizziness Care</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full p-4 bg-black/50 border border-gray-600 rounded-xl text-white focus:outline-none transition-colors resize-none"
                    style={{ ':focus': { borderColor: 'var(--brand-primary)' } }}
                    placeholder="Tell us about your condition or goals..."
                  ></textarea>
                </div>

                <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2">
                  Send Message
                  <Send size={18} />
                </button>
              </form>
            )}
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="heading-3 mb-8" style={{ color: 'var(--brand-primary)' }}>Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4 p-6 bg-gray-900/50 rounded-xl border" style={{ borderColor: 'var(--border-brick-subtle)' }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                    <Phone style={{ color: 'var(--brand-primary)' }} size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1" style={{ color: 'var(--brand-primary)' }}>Phone</h4>
                    <a href="tel:+14807579713" className="text-gray-300 hover:text-yellow-400 transition-colors">(480) 757-9713</a>
                    <p className="text-sm text-gray-400 mt-1">Available by appointment only - Tues/Thur 2pm-5pm</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-gray-900/50 rounded-xl border" style={{ borderColor: 'var(--border-brick-subtle)' }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                    <Mail style={{ color: 'var(--brand-primary)' }} size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1" style={{ color: 'var(--brand-primary)' }}>Email</h4>
                    <p className="text-gray-300">Info@gymratrehab.com</p>
                    <p className="text-sm text-gray-400">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-gray-900/50 rounded-xl border" style={{ borderColor: 'var(--border-brick-subtle)' }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                    <MapPin style={{ color: 'var(--brand-primary)' }} size={20} />
                  </div>
                  <div className="flex-1 flex items-start space-x-4">
                    <div className="flex-1">
                      <h4 className="font-semibold mb-1" style={{ color: 'var(--brand-primary)' }}>Location</h4>
                      <p className="text-gray-300"><a href="/favorite-places#alexander-medical-center" className="underline hover:no-underline" style={{ color: 'var(--brand-primary)' }}>Alexander Medical Center</a></p>
                      <p className="text-sm text-gray-400">2680 S Val Vista Dr Ste.131</p>
                      <p className="text-sm text-gray-400">Bldg. 6, Gilbert, AZ 85295</p>
                      <button 
                        onClick={() => window.open('https://maps.google.com/?q=2680+S+Val+Vista+Dr+Ste.131+Bldg.+6,+Gilbert,+AZ+85295', '_blank')}
                        className="mt-2 text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
                      >
                        Get Directions →
                      </button>
                    </div>
                    <div className="w-48 h-[8.75rem] rounded-lg overflow-hidden border flex-shrink-0" style={{ borderColor: 'var(--border-brick-subtle)' }}>
                      <iframe
                        src="https://maps.google.com/maps?width=100%25&amp;height=600&amp;hl=en&amp;q=2680%20S%20Val%20Vista%20Dr%20Ste.131%20Bldg.%206,%20Gilbert,%20AZ%2085295+(Alexander%20Medical%20Center)&amp;t=&amp;z=16&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Alexander Medical Center Location"
                      ></iframe>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-gray-900/50 rounded-xl border" style={{ borderColor: 'var(--border-brick-subtle)' }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                    <Clock style={{ color: 'var(--brand-primary)' }} size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1" style={{ color: 'var(--brand-primary)' }}>Hours</h4>
                    <p className="text-gray-300 mb-2">Available by appointment only</p>
                    <div className="text-gray-300 space-y-1">
                      <div className="flex justify-between">
                        <span>Tues / Thur</span>
                        <span>2:00 PM–5:00 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Mon / Wed / Fri–Sun</span>
                        <span>Closed</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Emergency Notice */}
            <div className="p-6 bg-red-900/20 border border-red-700 rounded-xl">
              <h4 className="font-bold text-red-400 mb-2">Medical Emergency?</h4>
              <p className="text-sm text-gray-300">
                For urgent medical needs, please call 911 or visit your nearest emergency room. 
                This contact form is for non-emergency appointments and questions only.
              </p>
            </div>
          </div>
        </div>
        </div>
      </section>
    </>
  );
};